const DEFAULT_ORDER = 9999;

function getOptionValue(item, valueKey) {
  if (item === null || item === undefined) {return "";}
  if (typeof item !== "object") {return String(item);}
  return String(item[valueKey] ?? item.id ?? item.value ?? "");
}

function getOptionLabel(item, labelKey) {
  if (item === null || item === undefined) {return "";}
  if (typeof item !== "object") {return String(item);}
  return String(item[labelKey] ?? item.name ?? item.label ?? item.email ?? item.id ?? item.value ?? "");
}

function applyValuePrefix(value, prefix = "") {
  if (!prefix || !value) {return value;}
  return `${prefix}${value}`;
}

function getOptionOrder(item) {
  if (!item || typeof item !== "object") {return DEFAULT_ORDER;}
  return Number(item.order ?? item.sortOrder ?? item.drugSortOrder ?? DEFAULT_ORDER);
}

function isArchivedOption(item) {
  if (!item || typeof item !== "object") {return false;}
  return item.archived === true ||
    item.isArchived === true ||
    item.hidden === true ||
    item.visible === false ||
    item.isActive === false;
}

function isInactiveOption(item) {
  return Boolean(item && typeof item === "object" && item.isActive === false);
}

function compareOptions(optionA, optionB) {
  const orderDiff = optionA.order - optionB.order;
  if (orderDiff !== 0) {return orderDiff;}
  return optionA.label.localeCompare(optionB.label);
}

function toSelectedValueSet(selectedValues = []) {
  const values = Array.isArray(selectedValues) ? selectedValues : [selectedValues];
  return new Set(values.map((value) => String(value)));
}

function getSourceValue(item, options = {}) {
  const valueKey = options.valueKey || "id";
  const resolvedValue = typeof options.valueResolver === "function" ?
    options.valueResolver(item) :
    getOptionValue(item, valueKey);
  return resolvedValue === null || resolvedValue === undefined ?
    "" :
    String(resolvedValue);
}

/**
 * Checks whether a config option is valid for a stream.
 * @param {object} item Source config option.
 * @param {string} [stream] Stream value to filter by.
 * @returns {boolean} True when unscoped, no stream is requested, or matched.
 */
export function optionSupportsStream(item, stream = "") {
  if (!stream) {return true;}
  const streams = Array.isArray(item?.streams) ?
    item.streams.map((entry) => String(entry)).filter(Boolean) :
    [];
  if (streams.length === 0) {return true;}
  return streams.includes(String(stream));
}

/**
 * Filters source options by stream while preserving already-selected values.
 * @param {Array<*>} items Source options.
 * @param {object} [options]
 * @param {string} [options.stream] Stream value.
 * @param {Array<string>|string} [options.selectedValues] Values to preserve.
 * @returns {Array<*>} Stream-compatible options plus selected saved values.
 */
export function filterOptionsByStream(items = [], options = {}) {
  const selectedValues = toSelectedValueSet(options.selectedValues);
  return (Array.isArray(items) ? items : []).filter((item) => {
    const value = getSourceValue(item, options);
    if (selectedValues.has(value)) {return true;}
    return optionSupportsStream(item, options.stream);
  });
}

/**
 * Normalises one source item into the standard control option shape.
 * @param {*} item Source item or primitive value.
 * @param {object} [options]
 * @param {string} [options.valueKey="id"] Object key to use as the stable value.
 * @param {string} [options.labelKey="name"] Object key to use as the visible label.
 * @param {string} [options.valuePrefix] Prefix to prepend to source values.
 * @param {Function} [options.valueResolver] Stable value resolver.
 * @param {Function} [options.labelResolver] Visible label resolver.
 * @param {string} [options.group] Static group label.
 * @param {Function} [options.groupResolver] Group label resolver.
 * @param {Function} [options.disabledResolver] Disabled-state resolver.
 * @param {Function} [options.archivedResolver] Archived-state resolver.
 * @param {Function} [options.accentResolver] Accent colour resolver.
 * @returns {object} Standard control option.
 */
export function normalizeControlOption(item, options = {}) {
  const valueKey = options.valueKey || "id";
  const labelKey = options.labelKey || "name";
  const resolvedValue = typeof options.valueResolver === "function" ?
    options.valueResolver(item) :
    getOptionValue(item, valueKey);
  const rawValue = resolvedValue === null || resolvedValue === undefined ?
    "" :
    String(resolvedValue);
  const value = applyValuePrefix(rawValue, options.valuePrefix);
  const label = typeof options.labelResolver === "function" ?
    String(options.labelResolver(item)) :
    getOptionLabel(item, labelKey);
  const group = typeof options.groupResolver === "function" ?
    options.groupResolver(item) :
    options.group || item?.group || item?.groupLabel || "";
  const archived = typeof options.archivedResolver === "function" ?
    Boolean(options.archivedResolver(item)) :
    isArchivedOption(item);
  const disabled = typeof options.disabledResolver === "function" ?
    Boolean(options.disabledResolver(item)) :
    Boolean(item?.disabled);
  const accentColor = typeof options.accentResolver === "function" ?
    options.accentResolver(item) :
    item?.color || item?.accentColor || item?.colour || "";
  const inactive = isInactiveOption(item);

  return {
    value,
    label,
    disabled,
    archived,
    inactive,
    hidden: Boolean(item?.hidden),
    group,
    accentColor,
    order: getOptionOrder(item),
    searchText: String(item?.searchText || `${label} ${value}`).toLowerCase(),
    source: item,
  };
}

/**
 * Normalises and filters a list into standard control options.
 * @param {Array<*>} items Source items.
 * @param {object} [options] Normalisation options.
 * @param {Array<string>|string} [options.selectedValues] Values that must remain visible.
 * @param {boolean} [options.includeArchived=false] Include archived or inactive source items.
 * @returns {Array<object>} Ordered standard control options.
 */
export function normalizeControlOptions(items = [], options = {}) {
  const selectedValues = toSelectedValueSet(options.selectedValues);
  return (Array.isArray(items) ? items : [])
    .map((item) => normalizeControlOption(item, options))
    .filter((option) => {
      if (!option.value && option.value !== "0") {return false;}
      if (options.includeArchived) {return true;}
      return !option.archived || selectedValues.has(String(option.value));
    })
    .map((option) => ({
      ...option,
      selectedOnly: option.archived && selectedValues.has(String(option.value)),
    }))
    .sort(compareOptions);
}

/**
 * Builds config-backed options without assuming specific status or priority IDs.
 * @param {object} config Runtime app config object.
 * @param {string} key Config list key, such as taskStatus or priorities.
 * @param {object} [options] Normalisation options.
 * @returns {Array<object>} Standard control options.
 */
export function createConfigOptions(config, key, options = {}) {
  return normalizeControlOptions(config?.[key] || [], {
    ...options,
    valueKey: options.valueKey || "id",
    labelKey: options.labelKey || "name",
  });
}

/**
 * Builds config-backed options filtered by stream while preserving saved values.
 * @param {object} config Runtime app config object.
 * @param {string} key Config list key, such as labStatus or maintenanceTypes.
 * @param {object} [options] Normalisation and stream-filter options.
 * @param {string} [options.stream] Current immutable stream value.
 * @param {Array<string>|string} [options.selectedValues] Values to preserve.
 * @returns {Array<object>} Standard control options.
 */
export function createStreamScopedConfigOptions(config, key, options = {}) {
  const selectedValues = toSelectedValueSet(options.selectedValues);
  return normalizeControlOptions(
    filterOptionsByStream(config?.[key] || [], {
      ...options,
      valueKey: options.valueKey || "id",
    }),
    {
      ...options,
      valueKey: options.valueKey || "id",
      labelKey: options.labelKey || "name",
    },
  ).map((option) => {
    const isSelected = selectedValues.has(String(option.value));
    if (!isSelected || optionSupportsStream(option.source, options.stream)) {
      return option;
    }

    return {
      ...option,
      selectedOnly: true,
      unavailableLabel: option.archived ? "archived" : "saved",
    };
  });
}

/**
 * Builds user options, excluding inactive users unless selected or requested.
 * @param {Array<object>} users App user records.
 * @param {object} [options] Normalisation options.
 * @param {boolean} [options.providersOnly=false] Restrict to app providers.
 * @returns {Array<object>} Standard control options.
 */
export function createUserOptions(users = [], options = {}) {
  const selectedValues = toSelectedValueSet(options.selectedValues);
  const filteredUsers = (Array.isArray(users) ? users : [])
    .filter((user) => {
      const isSelected = selectedValues.has(String(user?.id));
      if (options.providersOnly && user?.isAppProvider !== true && !isSelected) {return false;}
      if (options.includeInactive) {return true;}
      return user?.isActive !== false || isSelected;
    })
    .map((user) => {
      const {
        drugSortOrder: _drugSortOrder,
        order: _order,
        sortOrder: _sortOrder,
        ...userWithoutOrdering
      } = user || {};
      return userWithoutOrdering;
    });

  return normalizeControlOptions(filteredUsers, {
    ...options,
    group: options.group || "Users",
    archivedResolver: (user) => user?.isActive === false,
    labelKey: options.labelKey || "name",
  });
}

/**
 * Builds team options, excluding hidden teams unless selected or requested.
 * @param {Array<object>} teams App team records.
 * @param {object} [options] Normalisation options.
 * @returns {Array<object>} Standard control options.
 */
export function createTeamOptions(teams = [], options = {}) {
  const selectedValues = toSelectedValueSet(options.selectedValues);
  const filteredTeams = (Array.isArray(teams) ? teams : [])
    .filter((team) => {
      if (options.includeHidden) {return true;}
      return team?.visible !== false || selectedValues.has(String(team?.id));
    });

  return normalizeControlOptions(filteredTeams, {
    ...options,
    group: options.group || "Teams",
    archivedResolver: (team) => team?.visible === false,
    labelKey: options.labelKey || "name",
  });
}

/**
 * Builds generic domain options from app-owned source lists.
 * @param {Array<*>} items Domain source items.
 * @param {object} [options] Normalisation options.
 * @returns {Array<object>} Standard control options.
 */
export function createDomainOptions(items = [], options = {}) {
  return normalizeControlOptions(items, options);
}
